import React from "react";
import {useAppDispatch, useAppSelector} from "../../app/store";
import {CSSTransition} from "react-transition-group";
import {endGameSession, resetPointsDiff} from "../../app/slices/gameSession";
import {OverlayTrigger, Popover, ProgressBar} from "react-bootstrap";
import {GameSession} from "../../types/gameSession";
import {Language, translations} from "../../translations";
import powerByScope from "../../images/pb-scope-g.svg";

const describeSector = (cx: number, cy: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
        "M", cx, cy,
        "L", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        "Z"
    ].join(" ");
};

const polarToCartesian = (cx: number, cy: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
        x: cx + (radius * Math.cos(angleInRadians)),
        y: cy + (radius * Math.sin(angleInRadians))
    };
};

const percentToDegrees = (percent: number) => {
    // A full circle is 360 degrees
    const degrees = (percent / 100) * 360;
    return degrees;
};

const getSectorPathByPercent = (cx: number, cy: number, radius: number, percent: number) => {
    const startAngle = 0;
    const endAngle = percentToDegrees(percent);
    return describeSector(cx, cy, radius, startAngle, endAngle);
};

type CircleProgressProps = {
    session_data: GameSession;
    showPointsDiff: boolean;
    lang: Language
};
const CircleProgress = ({session_data, showPointsDiff, lang}: CircleProgressProps) => {
    const {data} = useAppSelector((state) => state.game);
    const displayCorrectAnswers = data?.display_correct_answers || false;

    const dispatch = useAppDispatch();
    const [previousPoints, setPreviousPoints] = React.useState(0);
    const [pointsDiff, setPointsDiff] = React.useState(0);

    const pointsDiffElemRef = React.useRef(null);

    // Get current point difference
    React.useEffect(() => {
        if (session_data.points !== null) {
            setPointsDiff(session_data.points - previousPoints);
            setPreviousPoints(session_data.points);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session_data.number, session_data.points]);

    const LANG = translations[lang];

    const totalPages = session_data.total;
    const pageNumber = session_data.number;
    const progress = (pageNumber / (totalPages + 1)) * 100;
    const points = displayCorrectAnswers ? session_data.points : null;

    const popOver = (
        <Popover id="popover-menu">
            <Popover.Header style={{textAlign: 'center'}} as="h3">{session_data.game.name}</Popover.Header>
            <Popover.Body className="menu-body">
                <p>{session_data.section.name}</p>
                <ProgressBar now={progress}/>
                <p className="page-progress">sivu {pageNumber}/{totalPages}</p>
                <a className="btn btn-outline-secondary btn-sm w-100 mt-2" onClick={() => dispatch(endGameSession())}>{LANG.quit_game}</a>
                <div className="powered-by">
                    <img src={powerByScope} alt="Powered by Scope"/>
                </div>
            </Popover.Body>
        </Popover>
    )

    return (
        <OverlayTrigger trigger="click" placement="top" overlay={popOver} rootClose>
            <div className='circle-progress'>
                <svg width={100} height={100} viewBox='0 0 100 100'>
                    <circle className='base' cx={50} cy={50} r={43}/>
                    {progress > 0 &&
                        <>
                            <circle className='progress-bg' cx={50} cy={50} r={48}/>
                            <circle className='progress'
                                    cx={50} cy={50} r={48}
                                    strokeDashoffset={100 - progress}
                                    strokeDasharray="100"
                                    pathLength="100"/>
                            <g transform="rotate(90 50 50)">
                                <path className="sector" d={getSectorPathByPercent(50, 50, 38, progress)}/>
                                <text x="50%" y="50%"
                                      className="page-number"
                                      textAnchor="middle"
                                      dy="30px">{pageNumber}/{totalPages}</text>
                                {points !== null &&
                                    <text x="50%" y="50%"
                                          className="points"
                                          textAnchor="middle"
                                          dy="12px">{points}</text>
                                }
                                <g transform="translate(85, 5)">
                                    <rect width="10" height="1" x="6" y="1" className="menu-line-shadow"/>
                                    <rect width="10" height="1" x="5" y="0" className="menu-line"/>
                                    <rect width="10" height="1" x="6" y="4" className="menu-line-shadow"/>
                                    <rect width="10" height="1" x="5" y="3" className="menu-line"/>
                                    <rect width="10" height="1" x="6" y="7" className="menu-line-shadow"/>
                                    <rect width="10" height="1" x="5" y="6" className="menu-line"/>
                                </g>
                                {points !== null &&
                                    <CSSTransition
                                        in={showPointsDiff}
                                        timeout={800}
                                        classNames="points-diff"
                                        onEntered={() => dispatch(resetPointsDiff())}
                                        nodeRef={pointsDiffElemRef}>
                                        <text x="50%" y="50%"
                                              ref={pointsDiffElemRef}
                                              className="points-diff"
                                              textAnchor="middle"
                                              dy="12px">{pointsDiff > 0 ? '+' : ''}{pointsDiff}</text>
                                    </CSSTransition>
                                }
                            </g>
                        </>
                    }
                </svg>
            </div>
        </OverlayTrigger>
    )
};

export default CircleProgress;